import { useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, animate, useInView } from 'framer-motion'
import { ArrowRight, Leaf, Lightbulb, Headphones, Download } from 'lucide-react'
import { SEOHead } from '@/components/SEOHead'
import { PartnersSection } from '@/components/sections/PartnersSection'
import { LocalizedLink } from '@/components/LocalizedLink'
import { certifications } from '@/data/partners'
import type { SupportedLanguage } from '@/i18n'

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
}

const stats = [
  { valueKey: 'about.statYears', labelKey: 'about.statYearsLabel' },
  { valueKey: 'about.statCountries', labelKey: 'about.statCountriesLabel' },
  { valueKey: 'about.statModels', labelKey: 'about.statModelsLabel' },
  { valueKey: 'about.statAccuracy', labelKey: 'about.statAccuracyLabel' },
]


const sustainabilityPillars = [
  { icon: Leaf, titleKey: 'about.sustainabilityPillar1Title', descKey: 'about.sustainabilityPillar1Desc' },
  { icon: Lightbulb, titleKey: 'about.sustainabilityPillar2Title', descKey: 'about.sustainabilityPillar2Desc' },
  { icon: Headphones, titleKey: 'about.sustainabilityPillar3Title', descKey: 'about.sustainabilityPillar3Desc' },
]

function AnimatedStat({ value }: { value: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, amount: 1 })

  // Parse: "25+", "50+", "9000+", "99.8%"
  const match = value.match(/^([^\d]*)(\d+(?:[.,]\d+)?)(.*)$/)
  const prefix = match?.[1] || ''
  const numStr = match?.[2] || '0'
  const suffix = match?.[3] || ''
  const target = parseFloat(numStr.replace(',', '.'))
  const decimals = numStr.includes('.') ? numStr.split('.')[1]?.length || 0
    : numStr.includes(',') ? numStr.split(',')[1]?.length || 0
    : 0
  const usesComma = numStr.includes(',')

  useEffect(() => {
    if (!isInView || !ref.current) return

    const controls = animate(0, target, {
      duration: 2,
      ease: 'easeOut',
      onUpdate(v) {
        if (!ref.current) return
        let formatted = decimals > 0 ? v.toFixed(decimals) : Math.round(v).toString()
        if (usesComma) formatted = formatted.replace('.', ',')
        ref.current.textContent = prefix + formatted + suffix
      },
    })

    return () => controls.stop()
  }, [isInView, target, prefix, suffix, decimals, usesComma])

  return <span ref={ref}>{value}</span>
}

export function AboutPage() {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'it') as SupportedLanguage

  return (
    <>
      <SEOHead titleKey="seo.about.title" descriptionKey="seo.about.description" />

      {/* ── 1. Hero — Statement Identitario ── */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/media/about/sede-selea.jpg"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/85 via-primary/70 to-primary/40" />
        </div>

        <div className="container-main relative z-10 py-20">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-accent font-semibold text-sm uppercase tracking-wider mb-4"
            >
              {t('about.eyebrow')}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-5xl font-bold text-white leading-tight mb-6"
            >
              {t('about.heroTitle')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg text-white/80 mb-8 leading-relaxed"
            >
              {t('about.heroSubtitle')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <LocalizedLink
                to={lang === 'en' ? '/products' : '/prodotti'}
                className="btn-primary"
              >
                {t('about.heroCta1')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </LocalizedLink>
              <LocalizedLink
                to={lang === 'en' ? '/contacts' : '/contatti'}
                className="btn-outline border-white text-white hover:bg-white hover:text-primary"
              >
                {t('about.heroCta2')}
              </LocalizedLink>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 2. Intro Azienda + Numeri + TTM Group ── */}
      <section className="section-padding bg-white">
        <div className="container-main">
          {/* Statement */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-bold text-center mb-12"
          >
            {t('about.statement')}
          </motion.h2>

          {/* Stats row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.valueKey}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="text-center"
              >
                <span className="block text-4xl md:text-5xl font-bold text-accent">
                  <AnimatedStat value={t(stat.valueKey)} />
                </span>
                <span className="text-text-muted text-sm mt-1 block">{t(stat.labelKey)}</span>
              </motion.div>
            ))}
          </div>

          {/* TTM Group — 2 col grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <img
                src="/media/about/ttm-group.jpg"
                alt={t('about.ttmTitle')}
                className="rounded-lg shadow-lg w-full"
                loading="lazy"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-4">{t('about.ttmTitle')}</h3>
              <p className="text-text-muted leading-relaxed">{t('about.ttmDesc')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 3. Competenze Chiave — Bento Grid ── */}
      <section className="section-padding bg-surface-light">
        <div className="container-main">
          {/* Bento grid: R&D tall left, Cameras + Software stacked right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">

            {/* R&D — hero card, tall, spans 2 rows */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="group relative lg:row-span-2 min-h-[420px] lg:min-h-0 rounded-2xl overflow-hidden cursor-default"
            >
              <img
                src="/media/about/ricerca-sviluppo-selea.jpg"
                alt={t('about.rdTitle')}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/5" />
              <span className="absolute top-6 right-8 text-[8rem] lg:text-[10rem] font-black leading-none text-white/[.06] select-none pointer-events-none">
                01
              </span>
              <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-10">
                <span className="inline-block text-accent text-xs font-bold uppercase tracking-widest mb-3">
                  R&amp;D
                </span>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
                  {t('about.rdTitle')}
                </h3>
                <p className="text-white/70 leading-relaxed max-w-md text-sm lg:text-base">
                  {t('about.rdDesc')}
                </p>
              </div>
            </motion.div>

            {/* Telecamere — top-right card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="group relative min-h-[280px] rounded-2xl overflow-hidden cursor-default"
            >
              <img
                src="/media/about/telecamere-videosorveglianza-selea.jpg"
                alt={t('about.camerasTitle')}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/5" />
              <span className="absolute top-5 right-6 text-[7rem] font-black leading-none text-white/[.06] select-none pointer-events-none">
                02
              </span>
              <div className="absolute inset-0 flex flex-col justify-end p-7">
                <span className="inline-block text-accent text-xs font-bold uppercase tracking-widest mb-2">
                  Hardware
                </span>
                <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">
                  {t('about.camerasTitle')}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed line-clamp-3">
                  {t('about.camerasDesc')}
                </p>
              </div>
            </motion.div>

            {/* Software — bottom-right card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="group relative min-h-[280px] rounded-2xl overflow-hidden cursor-default"
            >
              <img
                src="/media/about/software-solution-selea.jpg"
                alt={t('about.softwareTitle')}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/5" />
              <span className="absolute top-5 right-6 text-[7rem] font-black leading-none text-white/[.06] select-none pointer-events-none">
                03
              </span>
              <div className="absolute inset-0 flex flex-col justify-end p-7">
                <span className="inline-block text-accent text-xs font-bold uppercase tracking-widest mb-2">
                  Software
                </span>
                <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">
                  {t('about.softwareTitle')}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed line-clamp-3">
                  {t('about.softwareDesc')}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Assistenza — full-width cinematic card, horizontal layout */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="group relative mt-4 lg:mt-5 rounded-2xl overflow-hidden min-h-[260px] lg:min-h-[300px] cursor-default"
          >
            <img
              src="/media/about/assistenza-selea.jpg"
              alt={t('about.supportTitle')}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/10" />
            <span className="absolute top-6 right-8 text-[8rem] lg:text-[10rem] font-black leading-none text-white/[.06] select-none pointer-events-none">
              04
            </span>
            <div className="absolute inset-0 flex flex-col justify-center p-8 lg:p-12 max-w-2xl">
              <span className="inline-block text-accent text-xs font-bold uppercase tracking-widest mb-3">
                Support
              </span>
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
                {t('about.supportTitle')}
              </h3>
              <p className="text-white/70 leading-relaxed text-sm lg:text-base">
                {t('about.supportDesc')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 4. Sostenibilità ── */}
      <section className="relative section-padding bg-primary overflow-hidden">
        {/* Decorative circle */}
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container-main relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <img
                src="/media/sustainability/selea-sostenibilita.png"
                alt={t('about.sustainabilityTitle')}
                className="rounded-lg w-full"
                loading="lazy"
              />
            </motion.div>
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-3xl md:text-4xl font-bold text-white mb-4"
              >
                {t('about.sustainabilityTitle')}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-white/70 leading-relaxed mb-8"
              >
                {t('about.sustainabilityIntro')}
              </motion.p>
              <div className="space-y-4">
                {sustainabilityPillars.map((pillar, index) => (
                  <motion.div
                    key={pillar.titleKey}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="rounded-lg bg-white/5 border border-white/10 p-5 backdrop-blur-sm"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                        <pillar.icon className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-1">{t(pillar.titleKey)}</h4>
                        <p className="text-white/60 text-sm">{t(pillar.descKey)}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. Certificazioni ── */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-4xl font-bold mb-4"
            >
              {t('about.certificationsTitle')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-text-muted max-w-2xl mx-auto"
            >
              {t('about.certificationsIntro')}
            </motion.p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {certifications.map((cert, index) => {
              const Wrapper = cert.pdfUrl ? 'a' : 'div'
              const linkProps = cert.pdfUrl
                ? { href: cert.pdfUrl, target: '_blank' as const, rel: 'noopener noreferrer', download: '' }
                : {}
              return (
                <motion.div
                  key={cert.name}
                  custom={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <Wrapper
                    {...linkProps}
                    className={`group flex flex-col items-center text-center h-full p-6 rounded-lg border border-transparent hover:border-t-2 hover:border-t-accent hover:shadow-lg transition-all duration-300 ${cert.pdfUrl ? 'cursor-pointer' : ''}`}
                  >
                    <img
                      src={cert.logo}
                      alt={cert.name}
                      className="h-16 w-auto mb-4 object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                      loading="lazy"
                    />
                    <h4 className="text-sm font-semibold mb-1">{cert.name}</h4>
                    <p className="text-text-muted text-xs">{t(cert.descriptionKey)}</p>
                    {/* Spacer pushes download to bottom */}
                    <span className="flex-1" />
                    {cert.pdfUrl && (
                      <span className="inline-flex items-center gap-1.5 mt-4 text-xs font-medium text-accent group-hover:text-accent/80 transition-colors">
                        <Download className="w-3.5 h-3.5" />
                        {t('about.downloadCertificate')}
                      </span>
                    )}
                  </Wrapper>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── 6. Partners ── */}
      <PartnersSection />
    </>
  )
}
